import { useState, useEffect, useRef, useCallback } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTelegram } from "../contexts/TelegramContext";
import api from "../lib/api";
import { playNotificationSound, playChatSound, getSoundVolume } from "../lib/sounds";
import useLivePolling from "../hooks/useLivePolling";
import {
  ChartBar,
  Users,
  EnvelopeSimple,
  UserCircleGear,
  SignOut,
  List,
  X,
  House,
  PaperPlaneTilt,
  Gear,
  Package,
  Bell,
  ClockCounterClockwise,
  FileText,
  ChartLineUp,
  ShieldCheck,
  ClipboardText,
  Scroll,
  Clock,
  UsersThree,
  ChatCircle,
  DeviceMobile,
  CurrencyDollar,
  Truck,
  Recycle,
  Envelope,
  Crown,
  Receipt,
  CalendarStar,
  BellRinging,
  Storefront,
  Phone,
  Wallet,
  Headset,
  Note,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import CallBubble from "./dialer/CallBubble";
import { Avatar, AvatarFallback } from "../components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Badge } from "../components/ui/badge";

// iter: Bloque 2 — conservative first step, 3s -> 10s. Pauses while the
// tab is hidden and resumes on focus via useLivePolling.
const BADGE_POLL_INTERVAL_MS = 10000;

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: House, roles: ["admin", "bilingue", "asistente"] },
  // 🚨 ALERTA — sección operativa crítica: TODOS los roles deben revisarla
  // constantemente. Movida al tope (debajo de Dashboard) y con tema rojo
  // pulsante para que destaque sobre el resto del menú.
  { to: "/clientes-por-contactar", label: "Clientes por Contactar", icon: Phone, roles: ["admin", "asistente", "bilingue", "gerente"], verizonOnly: true, alert: true, allowAttTeam: true },
  { to: "/centro-llamadas", label: "Centro de Llamadas", icon: Headset, roles: ["admin", "gerente", "asistente", "bilingue"], verizonOnly: true, allowAttTeam: true },
  { to: "/clientes", label: "Clientes", icon: Users, roles: ["admin", "gerente", "bilingue", "asistente"], featureKey: "crm" },
  { to: "/agenda", label: "Agenda", icon: CalendarStar, roles: ["admin", "gerente", "asistente", "bilingue"], featureKey: "agenda_manual", hideForFounder: true, hideForAtt: true },
  { to: "/recordatorios", label: "Recordatorios", icon: BellRinging, roles: ["admin", "gerente"], featureKey: "reminders", hideForFounder: true },
  { to: "/ordenes", label: "Ordenes", icon: ClipboardText, roles: ["admin", "asistente", "bilingue", "gerente"], verizonOnly: true, allowAttTeam: true },
  { to: "/historial-ordenes", label: "Historial Ordenes", icon: Scroll, roles: ["admin", "asistente", "gerente"], verizonOnly: true, allowAttTeam: true },
  { to: "/catalogo", label: "Catálogo", icon: Storefront, roles: ["admin", "gerente", "asistente"], featureKey: "catalog", hideForFounder: true },
  { to: "/pedidos", label: "Pedidos", icon: ClipboardText, roles: ["admin", "gerente", "asistente"], featureKey: "orders", hideForFounder: true },
  { to: "/inventario", label: "Inventario", icon: Package, roles: ["admin", "gerente", "asistente"], featureKey: "inventory", hideForFounder: true },
  { to: "/enviar", label: "Enviar Correo", icon: PaperPlaneTilt, roles: ["admin", "bilingue", "asistente", "gerente"], featureKey: "email_templates" },
  { to: "/historial-correos", label: "Historial Correos", icon: ClockCounterClockwise, roles: ["admin", "bilingue", "asistente", "gerente"], featureKey: "email_templates" },
  { to: "/tracking", label: "Seguimientos", icon: Package, roles: ["admin", "asistente", "bilingue", "gerente"], verizonOnly: true, allowAttTeam: true },
  { to: "/etiquetas-reutilizables", label: "Etiquetas Reutilizables", icon: Recycle, roles: ["admin", "gerente"], verizonOnly: true, onlyFounderOrExtended: true, allowAttGerente: true },
  { to: "/solicitudes-etiqueta", label: "Solicitudes Etiqueta", icon: Envelope, roles: ["admin", "gerente"], hideForVerizonGerente: true },
  { to: "/etiquetas-shippo", label: "Etiquetas Shippo", icon: Truck, roles: ["admin", "gerente"], verizonOnly: true, onlyFounderOrExtended: true, hideForAtt: true },
  { to: "/notificaciones", label: "Notificaciones", icon: Bell, roles: ["admin", "bilingue", "asistente", "gerente"] },
  { to: "/notas", label: "Notas", icon: Note, roles: ["admin", "gerente", "asistente", "bilingue"] },
  { to: "/chat", label: "Chat", icon: ChatCircle, roles: ["admin", "bilingue", "asistente", "gerente"] },
  { to: "/correos", label: "Plantillas", icon: FileText, roles: ["admin"], featureKey: "email_templates", onlyFounder: true },
  { to: "/empleados", label: "Empleados", icon: UserCircleGear, roles: ["admin", "gerente"], onlyFounderOrExtended: true, allowAttGerente: true },
  { to: "/metricas", label: "Metricas", icon: ChartLineUp, roles: ["admin"], verizonOnly: true, onlyFounder: true },
  { to: "/metricas", label: "Metricas", icon: ChartLineUp, roles: ["gerente"], verizonOnly: true, allowAttTeam: true },
  { to: "/metricas", label: "Seguimiento Retornos", icon: ChartLineUp, roles: ["bilingue"], verizonOnly: true, allowAttTeam: true },
  { to: "/metricas", label: "Seguimiento Retornos", icon: ChartLineUp, roles: ["asistente"], verizonOnly: true, allowAttTeam: true },
  { to: "/mis-pagos", label: "Mis Pagos", icon: CurrencyDollar, roles: ["bilingue"], verizonOnly: true, allowAttTeam: true },
  { to: "/gastos", label: "Gastos", icon: Wallet, roles: ["admin", "gerente"], verizonOnly: true, onlyFounderOrExtended: true, allowAttGerente: true },
  // CC-8: a diferencia de Gastos (onlyFounderOrExtended), Finanzas es
  // visible para CUALQUIER gerente -- ve solo su propio team_id (forzado
  // por el backend, 403 real si intenta pedir otro equipo explicitamente).
  { to: "/finanzas", label: "Finanzas", icon: CurrencyDollar, roles: ["admin", "gerente"], verizonOnly: true, allowAttGerente: true },
  // Equipos — movido a Admin Central (/admin-central/equipos). El founder
  // ya no lo ve aquí; era onlyFounder de todas formas, así que nadie más
  // lo perdía.
  { to: "/auditoria", label: "Auditoria", icon: ShieldCheck, roles: ["admin"], onlyFounder: true },
  { to: "/dispositivos", label: "Aprobar Dispositivos", icon: DeviceMobile, roles: ["admin"], onlyFounder: true },
  { to: "/dispositivos-autorizados", label: "Dispositivos Autorizados", icon: DeviceMobile, roles: ["admin"], onlyFounder: true },
  { to: "/ajustes", label: "Ajustes", icon: Gear, roles: ["admin", "gerente"], onlyFounderOrExtended: true, allowAttGerente: true },
  // Facturación — consola founder movida a Admin Central
  // (/admin-central/facturacion). La vista gerente (ManagerStatus) sigue
  // viviendo en /billing dentro de Call Center — founderHide solo oculta
  // el link para el founder personal, gerente sigue viéndolo igual.
  { to: "/billing", label: "Facturación", icon: Receipt, roles: ["admin", "gerente"], founderHide: true },
  // Super Admin SaaS — solo founder (consulting@vzwhelpscenter.com)
  { to: "/super-admin", label: "Super Admin SaaS", icon: Crown, roles: ["admin"], onlyFounder: true },
];

const FOUNDER_EMAIL = "consulting@vzwhelpscenter.com";

const roleColors = {
  admin: "bg-primary/20 text-primary border-primary/30",
  gerente: "bg-purple-500/20 text-purple-400 border-purple-500/30",
  bilingue: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  asistente: "bg-amber-500/20 text-amber-400 border-amber-500/30",
};

// Menú lateral escondible (roles bilingue y asistente; el resto de roles sigue igual): arranca escondido y
// recuerda la preferencia del usuario entre visitas. Sin storage disponible,
// simplemente arranca escondido cada vez (no rompe la página).
const SIDEBAR_PREF_KEY = "cc.layout.menuOpen.bilingue";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // Módulo /chat (Telegram): pide pantalla/módulo completo, sin el padding/scroll
  // por defecto del <main>, para que ocupe todo el alto real disponible.
  const { pathname } = useLocation();
  const esPaginaCompleta = pathname === "/chat";
  const isBilingueRole = user?.role === "bilingue" || user?.role === "asistente";   // "menú escondible" (☰): bilingüe y asistente
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      if (user?.role === "bilingue" || user?.role === "asistente") return localStorage.getItem(SIDEBAR_PREF_KEY) === "1";
    } catch {}
    return false;
  });
  const [unreadCount, setUnreadCount] = useState(0);
  // El módulo Chat ahora es el Telegram del usuario: la insignia viene de su proveedor (ya no se consulta /chat/unread-count).
  const chatUnread = useTelegram().unreadTotal;
  const prevNotifRef = useRef(-1);
  const prevChatRef = useRef(-1);
  useEffect(() => {
    if (prevChatRef.current >= 0 && chatUnread > prevChatRef.current) playChatSound(getSoundVolume());
    prevChatRef.current = chatUnread;
  }, [chatUnread]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [featuresMatrix, setFeaturesMatrix] = useState(null);

  // Fetch the team feature matrix (modules enabled/locked per business type + plan)
  useEffect(() => {
    if (!user) return;
    api.get("/saas/me/features").then(r => setFeaturesMatrix(r.data)).catch(() => {});
  }, [user]);

  // Live clock - updates every second (AST = UTC-4)
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString("es-DO", { timeZone: "America/Santo_Domingo", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });
  };
  const formatDate = (date) => {
    return date.toLocaleDateString("es-DO", { timeZone: "America/Santo_Domingo", weekday: "short", day: "numeric", month: "short" });
  };

  const fetchCount = useCallback(async (isCancelled) => {
    try {
      const { data } = await api.get("/notifications/count");
      if (isCancelled?.()) return;
      const newCount = data.unread || 0;
      setUnreadCount(newCount);
      // Only play sound if count INCREASED after initial load
      if (prevNotifRef.current >= 0 && newCount > prevNotifRef.current) {
        playNotificationSound(getSoundVolume());
      }
      prevNotifRef.current = newCount;
    } catch {}
  }, []);

  // `refresh` re-runs fetchCount through the SAME in-flight guard as the
  // interval/visibility ticks, so an external "refresh now" trigger can't
  // create a second concurrent request for this poller.
  const refresh = useLivePolling(fetchCount, BADGE_POLL_INTERVAL_MS, [fetchCount], { skipInitial: false });

  // Listen for instant badge updates from other pages (e.g. after marking
  // a notification/message as read) — bypasses the poll interval's wait,
  // but not the in-flight guard.
  useEffect(() => {
    const handleBadgeUpdate = () => refresh();
    window.addEventListener("notif-badge-update", handleBadgeUpdate);
    return () => window.removeEventListener("notif-badge-update", handleBadgeUpdate);
  }, [refresh]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = user?.name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "U";

  const toggleBilingueMenu = () => {
    setSidebarOpen((prev) => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_PREF_KEY, next ? "1" : "0"); } catch {}
      return next;
    });
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background" data-testid="main-layout">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-60 bg-card border-r border-border flex flex-col transition-transform duration-150 ease-out ${
          isBilingueRole ? "" : "lg:relative lg:translate-x-0"
        } ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
        style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="sidebar"
      >
        <div className="flex items-center px-4 h-20 border-b border-border">
          <img src="/brand/commandix-logo-tight.png" alt="Commandix CRM" className="h-20 object-contain" draggable="false" data-testid="sidebar-branding-logo" />
        </div>

        {/* Switcher Admin Central ↔ Call Center — FASE 1. Solo el Founder
            literal (consulting@vzwhelpscenter.com) lo ve, nadie más: ni
            otros admins, ni gerente/asistente/bilingue de ningún team. */}
        {(user?.email || "").toLowerCase() === FOUNDER_EMAIL && (
          <div className="px-3 pt-3">
            <button
              type="button"
              onClick={() => navigate("/admin-central")}
              className="w-full flex items-center justify-center gap-2 text-xs font-semibold rounded-md border border-primary/30 bg-primary/10 hover:bg-primary/20 text-primary py-2 transition-colors"
              data-testid="switcher-to-admin-central"
            >
              <Crown size={14} weight="bold" />
              Ir a Admin Central
            </button>
          </div>
        )}

        <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
          {(() => {
            const founderEmail = (user?.email || "").toLowerCase() === FOUNDER_EMAIL;
            // STRICT founder = only the actual founder user. NOT every Verizon
            // team member. This is what controls "skip feature gating".
            // Without this, a Wester/Bryant member would skip the agenda_manual
            // filter and see Agenda even though their team doesn't have it.
            const isFounder = founderEmail;
            // Extended manager: a gerente whose team has team.extended_manager=true
            // (set by founder when granting "Commandix Manager" plan). They get the
            // same module visibility as founder for Shippo/Reusable/Employees/etc.
            // but the backend still enforces team_id isolation on each route.
            const isExtendedManager = !!featuresMatrix?.extended_manager && user?.role === "gerente";
            // iter157 Fase 1.5d — AT&T gerente: gets Empleados + Ajustes (scoped),
            // but NOT extended_manager (so Resend config stays hidden).
            const isAttGerente = user?.role === "gerente" && (featuresMatrix?.company === "att");
            // iter158 — AT&T team member (gerente, bilingue, asistente en team AT&T).
            // Necesario para que el bilingüe de Jhon vea Clientes por Contactar,
            // Ordenes, Seguimientos, Métricas, Mis Pagos — los módulos operativos
            // del CRM que existen tanto para Verizon como para AT&T.
            const isInAttTeam = (
                (user?.team_features?.company === "att")
                || (featuresMatrix?.company === "att")
            );
            // Verizon-style sidebar: founder always, plus members of any
            // team where the SaaS profile is `call_center` (Verizon helpers
            // like Hanoy's Beauty Suite are call_center too). Sub-tenants
            // like Josue are sub-tenants but operate the same business
            // model, so they need the same operational menu items
            // (Clientes por Contactar, Ordenes, Tracking, Shippo, etc.).
            // Backend still enforces team_id isolation per endpoint.
            const isInVerizonTeam = (
                founderEmail
                || !!featuresMatrix?.is_founder_team
                || (featuresMatrix?.business_type || "") === "call_center"
            );
            const enabledKeys = new Set((featuresMatrix?.enabled || []).map(m => m.key));
            return navItems
              .filter((item) => item.roles.includes(user?.role))
              // onlyFounder = ONLY the literal founder user (consulting@vzwhelpscenter.com)
              // can see this — not other admins of the same team.
              .filter((item) => !item.onlyFounder || founderEmail)
              // onlyFounderOrExtended = founder OR a gerente flagged extended_manager
              // can see this. Extended manager is identified by team.extended_manager=true.
              // iter157 Fase 1.5d — AT&T gerente also passes for Empleados + Ajustes
              // (marked in the item with `allowAttGerente`).
              .filter((item) => !item.onlyFounderOrExtended || founderEmail || isExtendedManager || (item.allowAttGerente && isAttGerente))
              // hideForFounder = ocultar a TODOS los miembros del equipo Verizon
              // (no solo al founder personal). Estos módulos son SaaS-only.
              .filter((item) => !item.hideForFounder || !isInVerizonTeam)
              // founderHide = ocultar SOLO al founder personal (a diferencia
              // de hideForFounder, que oculta a todo el equipo Verizon). Usado
              // para links que se movieron a Admin Central pero que otros
              // roles (ej. gerente) siguen necesitando en Call Center.
              .filter((item) => !item.founderHide || !founderEmail)
              // verizonOnly = visible to ALL members of the Verizon team.
              // iter158 — Also visible to ATT gerentes for items that opt-in
              // via `allowAttGerente` (e.g. Etiquetas Reutilizables) so Jhon
              // can access his own team's reusable labels. Y también a todo
              // el team AT&T (bilingues, asistentes) via `allowAttTeam` para
              // módulos operativos como Clientes por Contactar, Ordenes,
              // Tracking, Mis Pagos, Métricas.
              .filter((item) => !item.verizonOnly || isInVerizonTeam || (item.allowAttGerente && isAttGerente) || (item.allowAttTeam && isInAttTeam))
              // iter157 — hideForAtt = ocultar cuando el team.company === "att".
              // El founder (admin) sigue viendo estos módulos para operar los
              // equipos ATT desde el panel central. Etiquetas Shippo aplica:
              // los gerentes ATT solicitan la etiqueta, el admin la compra +
              // envía por WhatsApp.
              .filter((item) => {
                if (!item.hideForAtt) return true;
                if (isFounder) return true;
                // iter158 — Double-check AT&T detection using both team_features
                // (cargado en /auth/me) y featuresMatrix (cargado en /saas/me/features).
                // Jhon es AT&T + extended_manager; sin este guard doble podía
                // filtrarse el módulo si un feature u otro fallaba en cargar.
                const teamCompany = (
                    (user?.team_features?.company)
                    || (featuresMatrix?.company)
                    || "verizon"
                ).toLowerCase();
                return teamCompany !== "att";
              })
              // iter158 — hideForVerizonGerente = ocultar a gerentes de Verizon
              // (ej. Solicitudes de Etiqueta solo aplica a AT&T y admin).
              .filter((item) => {
                if (!item.hideForVerizonGerente) return true;
                if (isFounder) return true;
                if (user?.role !== "gerente") return true;
                const teamCompany = (
                    (user?.team_features?.company)
                    || (featuresMatrix?.company)
                    || "verizon"
                ).toLowerCase();
                return teamCompany !== "verizon";
              })
              .filter((item) => {
                // Feature gating only applies to non-founders. If matrix not loaded yet, show by default.
                if (isFounder) return true;
                if (!item.featureKey) return true;
                if (!featuresMatrix) return true;
                return enabledKeys.has(item.featureKey);
              });
          })().map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => {
                  if (item.alert) {
                    // 🚨 Sección alerta: rojo intenso, ring, ícono pulsante.
                    // Indica a TODOS los roles que esta sección debe revisarse
                    // constantemente — es la cola operativa del negocio.
                    return `relative flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-bold transition-all duration-150 border ${
                      isActive
                        ? "bg-red-500/25 text-red-200 border-red-500/70 shadow-[0_0_0_2px_rgba(239,68,68,0.35)]"
                        : "bg-red-500/15 text-red-200 border-red-500/50 hover:bg-red-500/25 hover:border-red-500/80 shadow-[0_0_0_1px_rgba(239,68,68,0.25)]"
                    }`;
                  }
                  return `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`;
                }}
                data-testid={`nav-${item.to.slice(1)}`}
              >
                <item.icon
                  size={20}
                  weight={item.alert ? "fill" : "duotone"}
                  className={item.alert ? "text-red-400 animate-pulse" : ""}
                />
                <span className="flex-1 flex items-center gap-2">
                  {item.label}
                  {item.alert && (
                    <span
                      className="text-[9px] font-extrabold tracking-wider px-1.5 py-0.5 rounded bg-red-500 text-white animate-pulse"
                      data-testid="nav-alert-badge"
                    >
                      ALERTA
                    </span>
                  )}
                </span>
                {item.to === "/notificaciones" && unreadCount > 0 && (
                  <span className="bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5" data-testid="nav-notif-badge">{unreadCount > 99 ? "99+" : unreadCount}</span>
                )}
                {item.to === "/chat" && chatUnread > 0 && (
                  <span className="bg-emerald-500 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5" data-testid="nav-chat-badge">{chatUnread > 99 ? "99+" : chatUnread}</span>
                )}
              </NavLink>
            ))}
        </nav>

        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3">
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-primary/20 text-primary text-xs font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${roleColors[user?.role] || ""}`}>
                {user?.role}
              </Badge>
            </div>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className={`fixed inset-0 z-40 bg-black/50 ${isBilingueRole ? "" : "lg:hidden"}`} onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <header
          className="border-b border-border bg-card flex items-center justify-between px-4 lg:px-6"
          style={{
            paddingTop: "env(safe-area-inset-top)",
            minHeight: "calc(4rem + env(safe-area-inset-top))",
          }}
          data-testid="topbar"
        >
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <X size={20} /> : <List size={20} />}
          </Button>

          {isBilingueRole && (
            <Button
              variant="ghost"
              size="icon"
              className="hidden lg:inline-flex"
              onClick={toggleBilingueMenu}
              aria-label="Mostrar u ocultar menú"
              data-testid="sidebar-toggle-desktop"
            >
              ☰
            </Button>
          )}

          <div className="flex-1 flex items-center justify-center">
            <div className="hidden sm:flex items-center gap-2 text-muted-foreground" data-testid="live-clock">
              <Clock size={16} weight="duotone" className="text-primary" />
              <span className="text-sm font-mono font-medium text-foreground">{formatTime(currentTime)}</span>
              <span className="text-xs text-muted-foreground">{formatDate(currentTime)}</span>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-trigger">
                <Avatar className="h-8 w-8">
                  <AvatarFallback className="bg-primary/20 text-primary text-xs font-bold">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-destructive cursor-pointer" data-testid="logout-button">
                <SignOut size={16} className="mr-2" />
                Cerrar Sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Page content */}
        <main
          className={
            esPaginaCompleta
              ? "flex-1 min-w-0 overflow-hidden flex flex-col"
              : "flex-1 min-w-0 overflow-y-auto p-4 lg:p-6"
          }
          data-testid="main-content"
        >
          {children}
        </main>
      </div>
      {/* Centro de Llamadas V4 (SIMULADO): burbuja persistente, fuera de las vistas del módulo. */}
      <CallBubble />
    </div>
  );
}
